/* eslint-disable lodash/prefer-lodash-method */
import './App.css';

import { initAll, track } from '@amplitude/unified';
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createQueryClient } from 'api/createQueryClient';
import TrialModal from 'components/TrialModal/TrialModal.component';
import { Tier } from 'consts/auth';
import { START_TRIAL_MODAL_ID, TRIAL_EXPIRED_MODAL_ID, UPGRADE_ACCESS_MODAL_ID } from 'consts/modals';
import { ModalsProvider, useModals } from 'contexts/modals';
import { useHasTier, useHealEmptyFirstTokenPermissions, useTrial } from 'hooks/use-auth';
import { PageLayout } from 'layout';
import { ErrorBoundary } from 'layout';
import { openMailto } from 'lib/mailto';
import React, { useEffect, useRef, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { Route, Router, Switch } from 'react-router-dom';
import { Routes } from 'routes';

import { history } from './lib/history';

// Lazy-loaded so it doesn't bloat the main bundle for normal users. Mounted
// at the top level in App.js (outside PageLayoutContainer) so the headless
// renderer gets bare chrome — no sidebar, Sign In button, or cookie banner.
const PrintArtifactPage = React.lazy(() => import('pages/PrintArtifactPage'));

//     : Determine where to put Amplitude LogOnMount

// Session replay is configured entirely in Amplitude (remote config):
// sampling is paced by Dynamic Sampling in Session Replay settings, which
// stays disabled if any SDK passes a local sampleRate — so no replay
// options here. (The unified SDK's key for them would be `sr`; a previous
// `sessionReplay` block was silently ignored.)
initAll(import.meta.env.VITE_AMPLITUDE_KEY, {
  analytics: {
    defaultTracking: {
      attribution: false,
      fileDownloads: false,
      formInteractions: false,
      sessions: true,
      pageViews: {
        trackOn: () => { return !window.location.pathname.includes('/callback'); },
        trackHistoryChanges: 'all',
        eventType: 'page_view',
      },
    },
  },
});

const onRedirectCallback = (appState?: AppState) => {
  // If using a Hash Router, you need to use window.history.replaceState to
  // remove the `code` and `state` query parameters from the callback url.
  // window.history.replaceState({}, document.title, window.location.pathname);
  history.replace((appState && appState.returnTo) || window.location.pathname);
};

// Create a client (wires Sentry capture into every query/mutation failure)
const queryClient = createQueryClient();

interface RootProviderProps {
  children: React.ReactNode;
}

const RootProvider = ({ children }: RootProviderProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ModalsProvider>
        <Router history={history}>
          <Auth0Provider
            domain={import.meta.env.VITE_AUTH_DOMAIN}
            clientId={import.meta.env.VITE_AUTH_CLIENT_ID}
            authorizationParams={{
              audience: import.meta.env.VITE_AUTH_API_URL,
              scope: 'openid profile email',
              redirect_uri: `${window.location.origin}/callback`,
            }}
            onRedirectCallback={onRedirectCallback}
            cacheLocation='localstorage'
            useRefreshTokens={true}
          >
            {children}
          </Auth0Provider>
        </Router>
      </ModalsProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
    </QueryClientProvider>
  );
};

const Page = () => {
  const [fromAuth0Login, setFromAuth0Login] = useState(false);
  const mountTracked = useRef(false);

  const { openModal, closeModal } = useModals();
  const trialAlertKey = 'trial-alert-time';

  const isFreeTier = useHasTier(Tier.Free);
  const trial = useTrial();
  useHealEmptyFirstTokenPermissions();

  useEffect(() => {
    const url = `${history.location.pathname}${history.location.search}`;
    const fromCallback = history.location.pathname.includes('/callback');
    if (fromCallback) setFromAuth0Login(true);

    // Fire once per SPA boot. The effect re-runs when fromAuth0Login /
    // isFreeTier flip as auth resolves, which used to re-track and inflate
    // app_mounted counts (each login boot also double-fired: login_callback,
    // then app_mounted after the redirect flipped fromAuth0Login).
    if (!mountTracked.current) {
      mountTracked.current = true;
      const loginCallbackEvent = { name: 'login_callback', props: {} };
      const appMountedEvent = { name: 'app_mounted', props: { url } };
      const event = fromCallback ? loginCallbackEvent : appMountedEvent;
      track(event.name, event.props);
    }

    const resetTrialAlert = () => {
      const time = new Date().getTime();
      localStorage.setItem(trialAlertKey, `${time}`);
      openModal(START_TRIAL_MODAL_ID);
    };

    // Check every 10h
    const checkAlertTime = (lastUpdated = 0, threshold = 10 * 60 * 60 * 1000) => {
      const now = new Date().getTime();
      const diff = now - lastUpdated;
      if (diff > threshold) resetTrialAlert();
    };

    if (isFreeTier) {
      if (fromAuth0Login) resetTrialAlert();
      const lastTrialAlertTime = localStorage.getItem(trialAlertKey);
      if (lastTrialAlertTime) checkAlertTime(+lastTrialAlertTime);
    }
  }, [openModal, fromAuth0Login, isFreeTier]);

  useEffect(() => {
    if (trial && trial.isExpired) {
      openModal(TRIAL_EXPIRED_MODAL_ID);
    }
  }, [openModal, trial]);

  const salesEmail = 'sales@oilytics.co';

  return (
    <React.Fragment>
      <DocumentTitle title='HiLo Analytics'>
        {/*
          /print/artifact is intercepted here, BEFORE PageLayoutContainer, so
          the headless renderer gets a bare page (no sidebar, Sign In button,
          or cookie banner). Auth comes from the ?token= query param, not from
          PageLayout's Auth0 session, so PageLayout would only get in the way.
        */}
        <Switch>
          <Route exact path='/print/artifact'>
            {/*
              Tailwind's preflight normalizes the browser body margin, so the
              screenshot has no extra white border. ErrorBoundary keeps
              render-time chart failures from taking down the whole tree —
              without it the renderer would never see __artifactReady and the
              screenshot would time out.
            */}
            <ErrorBoundary>
              <React.Suspense fallback={null}>
                <PrintArtifactPage />
              </React.Suspense>
            </ErrorBoundary>
          </Route>
          <Route>
            <PageLayout>
              <Routes/>
            </PageLayout>
          </Route>
        </Switch>
      </DocumentTitle>
      <TrialModal
        id={START_TRIAL_MODAL_ID}
        title="Start a trial for full access"
        content={`Contact us to start a 2 week trial with full access to daily market
        reports, pricing dashboards, fundamentals, and more`}
        acceptText='Start trial'
        cancelText='Later'
        onClose={(accepted) => {
          closeModal(START_TRIAL_MODAL_ID);
          if (accepted) {
            openMailto(salesEmail, 'HiLo Trial Access', 'Hello! I\'m interested in a 14 day trial');
          }
        }}
      />
      <TrialModal
        id={TRIAL_EXPIRED_MODAL_ID}
        title='Trial expired'
        content={`Contact us to become a full subscriber with access to daily
        market reports, pricing dashboards, fundamentals, and more`}
        acceptText='Contact us'
        cancelText='Not now'
        onClose={(accepted) => {
          closeModal(TRIAL_EXPIRED_MODAL_ID);
          if (accepted) {
            openMailto(salesEmail, 'HiLo Full Access', 'Hello! I\'m interested in a full subscription');
          }
        }}
      />
      <TrialModal
        id={UPGRADE_ACCESS_MODAL_ID}
        title='Upgrade'
        content={`Contact us to become a full subscriber with access to daily
        market reports, pricing dashboards, fundamentals, and more`}
        acceptText='Contact us'
        cancelText='Not now'
        onClose={(accepted) => {
          closeModal(UPGRADE_ACCESS_MODAL_ID);
          if (accepted) {
            openMailto(salesEmail, 'HiLo Full Access', 'Hello! I\'m interested in a full subscription');
          }
        }}
      />
    </React.Fragment>
  );
};

export const App = () => {
  return (
    <RootProvider>
      <Page />
    </RootProvider>
  );
};
